import React, { useRef, useState } from "react";
import * as tf from "@tensorflow/tfjs";

const TreeVerticalScanner = () => {
  const [imageURL, setImageURL] = useState(null);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (file) setImageURL(URL.createObjectURL(file));
  };

  const scanVerticalSections = async () => {
    if (!imageRef.current || !canvasRef.current) return;

    const img = imageRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // 1. Force canvas to full raw image resolution
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    canvas.width = width;
    canvas.height = height;

    // 2. Paint the high-res image to canvas first
    ctx.drawImage(img, 0, 0);

    // 3. CRITICAL FIX: Read pixels from the CANVAS, not the squished HTML <img>
    const baseTensor = tf.browser.fromPixels(canvas);

    // Define 5 vertical columns (1/5 sections)
    const numSections = 5;
    const sectionWidth = Math.floor(width / numSections);

    ctx.strokeStyle = "#9932CC"; // Purple wireframe
    ctx.lineWidth = Math.max(width / 200, 5);
    ctx.lineJoin = "round";

    // ... rest of your loop code stays exactly the same ...

    // 2. Loop through each of the 1/5 vertical strips
    for (let s = 0; s < numSections; s++) {
      const startX = s * sectionWidth;

      await tf.tidy(() => {
        // Slice out the 1/5 vertical section tensor
        const sectionTensor = baseTensor.slice(
          [0, startX, 0],
          [height, sectionWidth, 3]
        );

        // Normalize inputs between 0 and 1 (Padding/Scaling transformation)
        const normalized = sectionTensor.toFloat().div(tf.scalar(255));

        // Split channels to isolate tree "essence" (Green/Red contrast over Blue sky/shadows)
        const [r, g, b] = tf.split(normalized, 3, 2);

        // Custom Kernel Math: Highlight tree organic matter vs surroundings
        const treeEssence = g
          .add(r)
          .sub(b.mul(tf.scalar(1.5)))
          .squeeze();

        // Apply ReLU activation: Eliminates negative background noise, keeping only standing objects
        const reluActivated = tf.relu(treeEssence);

        // Apply Sigmoid activation: Compresses features between 0 and 1 to find the curvature profile
        const sigmoidCurvature = tf.sigmoid(
          reluActivated.sub(tf.scalar(0.5)).mul(tf.scalar(10))
        );

        // Synchronize tensor data back to loops for coordinate mapping
        const maskValues = sigmoidCurvature.dataSync();

        const leftPoints = [];
        const rightPoints = [];

        // Stride vertically down the column to find trunk edges
        const verticalStride = Math.max(1, Math.floor(height / 20));
        for (let y = 0; y < height; y += verticalStride) {
          let firstEdge = null;
          let lastEdge = null;

          for (let x = 0; x < sectionWidth; x++) {
            const index = y * sectionWidth + x;
            // Threshold based on our Sigmoid curve analysis
            if (maskValues[index] > 0.7) {
              if (firstEdge === null) firstEdge = x;
              lastEdge = x;
            }
          }

          if (firstEdge !== null) {
            leftPoints.push({ x: startX + firstEdge, y: y });
            rightPoints.push({ x: startX + lastEdge, y: y });
          }
        }

        // 3. Draw curved vertical boundaries for this section
        if (leftPoints.length > 1) {
          ctx.beginPath();
          ctx.moveTo(leftPoints[0].x, leftPoints[0].y);
          for (let p of leftPoints) ctx.lineTo(p.x, p.y);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(rightPoints[0].x, rightPoints[0].y);
          for (let p of rightPoints) ctx.lineTo(p.x, p.y);
          ctx.stroke();
        }
      });
    }

    baseTensor.dispose();

    // 4. Automatically Export Fused Output
    const link = document.createElement("a");
    link.download = "tree_vertical_wireframe.png";
    link.href = canvas.toDataURL();
    link.click();
  };

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#111",
        color: "#fff",
        fontFamily: "sans-serif",
      }}
    >
      <h3>1/5 Vertical Column Tensor Scanner (ReLU + Sigmoid)</h3>
      <input type="file" accept="image/*" onChange={handleUpload} />
      {imageURL && (
        <div style={{ marginTop: "20px" }}>
          <img
            ref={imageRef}
            src={imageURL}
            alt="Source"
            style={{ maxWidth: "100%", maxHeight: "400px" }}
          />
          <button
            onClick={scanVerticalSections}
            style={{
              display: "block",
              marginTop: "15px",
              padding: "10px",
              backgroundColor: "#9932CC",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Generate Curved Wireframe
          </button>
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

export default TreeVerticalScanner;
